import './AuditTrailsTable.scss';

import { Box, Header, Pagination, Table } from "@amzn/awsui-components-react";
import { FunctionComponent, useEffect } from "react";

import { buildColumnDefinitions, getString } from "../../common";
import EmptyTableBox from "../../common/EmptyTableBox/EmptyTableBox";
import { ColumnSetting } from "../../common/utils/config-models";
import auditTrailsTableConfig from "./audit-trails.table-config.json";
import { useGetAuditTrails } from "../../hooks/audit-trails-hooks";
import { Relation } from "@amzn/ito-client";
import { formatFieldPath, formatSource, formatValue } from "../../common/utils/audit-trail-formatters";

const columns: ColumnSetting[] = auditTrailsTableConfig.columns;

const cellRenderers: Record<string, (item: any) => JSX.Element> = {
    auditField: (item) => {
        const lines = formatFieldPath(item.field ?? "").split("\n").slice(0, 3);
        return (
            <span>
                {lines.map((line, i) => (
                    <span key={i} className="audit-field-line" style={{ paddingLeft: `${i * 12}px` }}>
                        {line}
                    </span>
                ))}
            </span>
        );
    },
    auditSource: (item) => <span>{formatSource(item.source ?? "")}</span>,
    auditPreviousValue: (item) => <pre className="audit-value-pre">{formatValue(item.previousValue ?? "")}</pre>,
    auditNewValue: (item) => <pre className="audit-value-pre">{formatValue(item.newValue ?? "")}</pre>,
};

export interface AuditTrailsTableProps {
    orderId: string | undefined;
    orderConsumer: string | undefined;
    orderRelations: Relation[]
}

export const AuditTrailsTable: FunctionComponent<AuditTrailsTableProps> = (props: AuditTrailsTableProps) => {
    const currentColumnDefinitions = buildColumnDefinitions(columns).map((colDef) => {
        const renderer = colDef.id ? cellRenderers[colDef.id] : undefined;
        return renderer ? { ...colDef, cell: renderer } : colDef;
    });

    const { data, pagination, doGetAuditTrails } = useGetAuditTrails({
        orderId: props.orderId ?? '',
        orderConsumer: props.orderConsumer ?? '',
        orderRelations: props.orderRelations
    });

    useEffect(() => {
        if(props.orderId !== undefined) {
            doGetAuditTrails();
        }
    }, [props.orderId]);

    return (
        <Box margin={{ bottom: "m" }}>
            <Table
                data-testid="order-items-table"
                empty={<EmptyTableBox />}
                header={
                    <Header>{getString('auditTrails.table.headerTitle')}</Header>
                }
                columnDefinitions={currentColumnDefinitions}
                loading={data.isLoadingAudit}
                loadingText={getString('common.loading')}
                items={data.auditTrails}
                resizableColumns={true}
                stickyHeader={true}
                variant="full-page"
                wrapLines
                pagination={
                    <Pagination
                        currentPageIndex={pagination.currentPage.index}
                        pagesCount={pagination.pagesCount}
                        onChange={pagination.onChangePageHandler}
                    />
                }  
            />
        </Box>
    );
};
